'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

interface BattleItem {
  id: string;
}

interface AnimatedBattleListProps<T extends BattleItem> {
  battles: T[] | undefined;
  renderBattle: (battle: T) => ReactNode;
  renderEmpty?: () => ReactNode;
  className?: string;
}

const MOVE_MS = 280;
const EXIT_MS = 240;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const GAP_PX = 8;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface LayoutBox {
  top: number;
  height: number;
}

const findScroller = (el: HTMLElement | null): HTMLElement | null => {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
};

export function AnimatedBattleList<T extends BattleItem>({
  battles,
  renderBattle,
  renderEmpty,
  className,
}: AnimatedBattleListProps<T>) {
  const [gone, setGone] = useState<Set<string>>(() => new Set());
  const listRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const nodes = useRef(new Map<string, HTMLDivElement>());
  const lastLayout = useRef(new Map<string, LayoutBox>());
  const prevDisplay = useRef<T[] | null>(null);
  const exitTimers = useRef(new Map<string, number>());

  const currentIds = useMemo(() => new Set((battles ?? []).map((b) => b.id)), [battles]);

  const display = useMemo(() => {
    const result = [...(battles ?? [])];
    const prev = prevDisplay.current ?? [];
    prev.forEach((b, i) => {
      if (currentIds.has(b.id) || gone.has(b.id)) return;
      const placed = new Set(result.map((r) => r.id));
      const before = prev.slice(0, i).reverse().find((p) => placed.has(p.id));
      const at = before ? result.findIndex((r) => r.id === before.id) + 1 : 0;
      result.splice(at, 0, b);
    });
    return result;
  }, [battles, currentIds, gone]);

  const addedIds = useMemo(() => {
    const prev = prevDisplay.current;
    if (!prev || !battles) return new Set<string>();
    const prevIds = new Set(prev.map((b) => b.id));
    return new Set(battles.filter((b) => !prevIds.has(b.id)).map((b) => b.id));
  }, [battles]);

  const setNode = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) nodes.current.set(id, el);
    else nodes.current.delete(id);
  }, []);

  useLayoutEffect(() => {
    prevDisplay.current = display;
  }, [display]);

  useLayoutEffect(() => {
    const scroller = findScroller(listRef.current);
    scrollerRef.current = scroller;
    // WHY: Chromium якорит прокрутку сам (overflow-anchor: auto), WebKit нет — нативное выключаем и якорим сами
    if (scroller) scroller.style.overflowAnchor = 'none';
  }, []);

  // WHY: вебвью Telegram на iOS не держит контент под пальцем при вставке/удалении выше вьюпорта
  useLayoutEffect(() => {
    const reduced = prefersReducedMotion();
    const list = listRef.current;
    const scroller = scrollerRef.current;
    const listTop = list?.getBoundingClientRect().top ?? 0;
    const layout = new Map<string, LayoutBox>();
    nodes.current.forEach((el, id) => layout.set(id, { top: el.offsetTop, height: el.offsetHeight }));

    const exiting = exitTimers.current.size > 0;

    let anchored = false;
    if (!exiting && scroller && list && scroller.scrollTop > 0 && prevDisplay.current) {
      const viewTop = scroller.getBoundingClientRect().top - listTop;
      const anchor = prevDisplay.current.find((b) => {
        const last = lastLayout.current.get(b.id);
        return last !== undefined && layout.has(b.id) && last.top + last.height > viewTop;
      });
      if (anchor) {
        const delta = layout.get(anchor.id)!.top - lastLayout.current.get(anchor.id)!.top;
        if (delta !== 0) {
          scroller.scrollTop += delta;
          anchored = true;
        }
      }
    }

    if (!reduced && !exiting && !anchored) {
      nodes.current.forEach((el, id) => {
        const last = lastLayout.current.get(id);
        const top = layout.get(id)!.top;
        if (last === undefined || typeof el.animate !== 'function') return;
        const visualOffset = el.getBoundingClientRect().top - listTop - top;
        const dy = last.top - top + visualOffset;
        if (Math.abs(dy) < 1) return;
        el.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }], { duration: MOVE_MS, easing: EASING });
      });
    }
    lastLayout.current = layout;
  });

  useLayoutEffect(() => {
    const finish = (id: string, animated: boolean) => {
      exitTimers.current.delete(id);
      if (animated) nodes.current.forEach((el, nodeId) => lastLayout.current.set(nodeId, { top: el.offsetTop, height: el.offsetHeight }));
      setGone((g) => new Set(g).add(id));
    };

    const aboveViewport = (el: HTMLElement) => {
      const scroller = scrollerRef.current;
      return scroller !== null && el.getBoundingClientRect().bottom < scroller.getBoundingClientRect().top;
    };

    display.forEach((b) => {
      const id = b.id;
      const isLeaving = !currentIds.has(id);
      const pending = exitTimers.current.get(id);
      const el = nodes.current.get(id);

      if (!isLeaving && pending !== undefined) {
        window.clearTimeout(pending);
        exitTimers.current.delete(id);
        if (el) {
          el.getAnimations().forEach((a) => a.cancel());
          el.style.overflow = '';
        }
        return;
      }
      if (!isLeaving || pending !== undefined) return;

      const duration = prefersReducedMotion() || !el || typeof el.animate !== 'function' || aboveViewport(el) ? 0 : EXIT_MS;
      if (el && duration > 0) {
        el.style.overflow = 'hidden';
        el.animate(
          [
            { height: `${el.offsetHeight}px`, opacity: 1, marginBottom: '0px' },
            { height: '0px', opacity: 0, marginBottom: `${-GAP_PX}px` },
          ],
          { duration, easing: EASING, fill: 'forwards' },
        );
      }
      exitTimers.current.set(id, window.setTimeout(() => finish(id, duration > 0), duration));
    });

    const shown = new Set(display.map((b) => b.id));
    setGone((g) => {
      const next = new Set([...g].filter((id) => shown.has(id)));
      return next.size === g.size ? g : next;
    });
  }, [display, currentIds]);

  useLayoutEffect(() => {
    const timers = exitTimers.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const isEmpty = display.length === 0;

  return (
    <div ref={listRef} className={`relative flex flex-col gap-2 ${className || ''}`}>
      {display.map((battle) => (
        <div
          key={battle.id}
          ref={(el) => setNode(battle.id, el)}
          className={addedIds.has(battle.id) ? 'animate-shot-battle-item-in' : undefined}
        >
          {renderBattle(battle)}
        </div>
      ))}
      {isEmpty && renderEmpty?.()}
    </div>
  );
}
